import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useRole } from "../context/RoleContext";
import { getAdminUser, deactivateUser, reactivateUser } from "../lib/adminPortal";
import { ChevronRight } from "lucide-react";
import { relativeDate } from "../lib/ownerUi";
import BackBar from "../components/BackBar";

const ROLE_TONE = {
  owner_builder: { fg: "#1B1F4B", bg: "rgba(91,97,150,0.12)" },
  architect: { fg: "#8F3C23", bg: "rgba(177,78,47,0.10)" },
  city_staff: { fg: "#3A5563", bg: "rgba(58,85,99,0.10)" },
  platform_admin: { fg: "#1B1F4B", bg: "rgba(27,31,75,0.10)" },
};

export default function AdminUserDetail() {
  const { userId: targetId } = useParams();
  const { user } = useRole();
  const [u, setU] = useState(null);
  const [err, setErr] = useState(null);
  const [showDeact, setShowDeact] = useState(false);
  const [showReact, setShowReact] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    try {
      const next = await getAdminUser(targetId, user.id);
      setU(next);
    } catch (e) {
      setErr(e);
    }
  };

  useEffect(() => {
    if (!user?.id) return;
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, targetId]);

  if (err) return <p style={{ color: "#A23B2E" }} data-testid="admin-user-error">Could not load this user.</p>;
  if (!u) return <div style={{ color: "#6C6B85", fontStyle: "italic" }} data-testid="admin-user-loading">Loading user…</div>;

  const isDeactivated = u.account_status === "deactivated";
  const tone = ROLE_TONE[u.role] || ROLE_TONE.owner_builder;
  const isSelf = u.id === user?.id;

  const onDeactivate = async () => {
    setBusy(true);
    try {
      await deactivateUser(targetId, user.id, reason.trim());
      setShowDeact(false);
      setReason("");
      await refresh();
    } finally {
      setBusy(false);
    }
  };

  const onReactivate = async () => {
    setBusy(true);
    try {
      await reactivateUser(targetId, user.id, reason.trim());
      setShowReact(false);
      setReason("");
      await refresh();
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="animate-fade-in-up" data-testid="admin-user-detail">
      <BackBar
        parent={{ to: "/admin/users", label: "Users" }}
        trail={[
          { label: "Admin", to: "/admin" },
          { label: "Users", to: "/admin/users" },
          { label: u.name },
        ]}
      />

      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1
            style={{
              fontFamily: '"Fraunces", Georgia, serif',
              fontSize: "clamp(26px, 3.6vw, 32px)",
              fontWeight: 600,
              color: "#1B1F4B",
            }}
            data-testid="admin-user-name"
          >
            {u.name}
          </h1>
          <div className="mt-2 flex items-center gap-2 flex-wrap">
            <span
              style={{
                padding: "2px 8px",
                borderRadius: 999,
                background: tone.bg,
                color: tone.fg,
                fontSize: 10.5,
                fontWeight: 700,
                letterSpacing: "0.05em",
                textTransform: "uppercase",
              }}
            >
              {u.role.replace(/_/g, " ")}
            </span>
            <span
              style={{
                padding: "2px 8px",
                borderRadius: 999,
                background: isDeactivated ? "rgba(154,160,192,0.30)" : "rgba(79,97,73,0.10)",
                color: isDeactivated ? "#6C6B85" : "#4F6149",
                fontSize: 10.5,
                fontWeight: 700,
                letterSpacing: "0.05em",
                textTransform: "uppercase",
              }}
              data-testid="admin-user-status"
            >
              {isDeactivated ? "Deactivated" : "Active"}
            </span>
          </div>
          <div
            style={{
              fontFamily: '"Newsreader", Georgia, serif',
              fontStyle: "italic",
              color: "#6C6B85",
              fontSize: 14,
              marginTop: 4,
            }}
          >
            {u.organization || u.firm || "—"}
          </div>
        </div>
      </div>

      <div className="mt-7 grid lg:grid-cols-[3fr_2fr] gap-7">
        <div className="space-y-6">
          <div className="wn-card" style={{ padding: 18 }} data-testid="admin-user-identity">
            <h2
              style={{
                fontFamily: '"Fraunces", Georgia, serif',
                fontWeight: 600,
                fontSize: 18,
                color: "#1B1F4B",
              }}
            >
              Identity.
            </h2>
            <dl
              className="mt-3 grid grid-cols-2 gap-y-2 gap-x-6"
              style={{ fontSize: 13, color: "#272636" }}
            >
              <K label="Name" v={u.name} />
              <K label="Email" v={u.email} />
              <K label="Phone" v={u.phone} />
              <K label="Organization" v={u.organization || u.firm || "—"} />
              <K label="Role" v={u.role.replace(/_/g, " ")} />
              <K label="Created" v={u.created_at ? relativeDate(u.created_at) : "—"} />
            </dl>
            <p style={{ color: "#6C6B85", fontSize: 11.5, marginTop: 10 }}>
              Identity and role fields are read-only. Account creation and role editing are
              out of scope for the admin portal.
            </p>
          </div>

          {u.role_specific && Object.keys(u.role_specific).length > 0 && (
            <div className="wn-card" style={{ padding: 18 }} data-testid="admin-user-role-context">
              <h2
                style={{
                  fontFamily: '"Fraunces", Georgia, serif',
                  fontWeight: 600,
                  fontSize: 18,
                  color: "#1B1F4B",
                }}
              >
                Role context.
              </h2>
              <RoleContext role={u.role} ctx={u.role_specific} />
            </div>
          )}

          <div className="wn-card" style={{ padding: 18 }} data-testid="admin-user-activity">
            <h2
              style={{
                fontFamily: '"Fraunces", Georgia, serif',
                fontWeight: 600,
                fontSize: 18,
                color: "#1B1F4B",
              }}
            >
              Recent activity.
            </h2>
            <div className="mt-3 space-y-2">
              {(u.recent_activity || []).length === 0 && (
                <div style={{ color: "#6C6B85", fontSize: 13 }}>No recent activity recorded.</div>
              )}
              {(u.recent_activity || []).map((a, i) => (
                <div key={a.id || i} className="flex items-start gap-3">
                  <span
                    aria-hidden
                    style={{ marginTop: 6, width: 8, height: 8, borderRadius: 999, background: "#B14E2F", flexShrink: 0 }}
                  />
                  <div>
                    <div style={{ fontSize: 12.5, color: "#272636" }}>{prettyAction(a.action)}</div>
                    <div
                      style={{
                        fontFamily: '"Newsreader", Georgia, serif',
                        fontStyle: "italic",
                        color: "#6C6B85",
                        fontSize: 11.5,
                      }}
                    >
                      {relativeDate(a.at)} · {a.record_type || "—"}
                    </div>
                    {a.note && (
                      <div style={{ color: "#272636", fontSize: 12, marginTop: 2 }}>{a.note}</div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <aside data-testid="admin-user-actions">
          <div className="wn-card" style={{ padding: 18 }}>
            <h2
              style={{
                fontFamily: '"Fraunces", Georgia, serif',
                fontWeight: 600,
                fontSize: 18,
                color: "#1B1F4B",
              }}
            >
              Account Actions.
            </h2>

            {!isDeactivated && !showDeact && (
              <>
                <p style={{ color: "#6C6B85", fontSize: 12.5, marginTop: 4 }}>
                  Deactivation is reversible. The user cannot sign in or take any action
                  while deactivated; existing records are preserved.
                </p>
                <button
                  className="wn-btn wn-btn-tertiary mt-3 w-full"
                  style={{ color: "#A23B2E", justifyContent: "center" }}
                  onClick={() => setShowDeact(true)}
                  disabled={isSelf}
                  title={isSelf ? "You cannot deactivate yourself" : undefined}
                  data-testid="admin-user-deactivate-open"
                >
                  Deactivate Account
                </button>
                {isSelf && (
                  <p style={{ color: "#A23B2E", fontSize: 11.5, marginTop: 6, textAlign: "center" }}>
                    You cannot deactivate yourself.
                  </p>
                )}
              </>
            )}

            {!isDeactivated && showDeact && (
              <div className="mt-3" data-testid="admin-user-deactivate-form">
                <label className="wn-label block">Reason (≥ 20 chars)</label>
                <textarea
                  className="wn-input"
                  style={{ minHeight: 100, resize: "vertical" }}
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  data-testid="admin-deactivate-reason"
                />
                <div
                  className="tabular-nums"
                  style={{ fontSize: 11, color: reason.trim().length < 20 ? "#A23B2E" : "#6C6B85", marginTop: 2 }}
                >
                  {reason.trim().length} / 20 characters
                </div>
                <div className="mt-3 flex justify-end gap-2">
                  <button
                    className="wn-btn wn-btn-tertiary"
                    onClick={() => { setShowDeact(false); setReason(""); }}
                  >
                    Cancel
                  </button>
                  <button
                    className="wn-btn"
                    style={{ background: "#A23B2E", color: "#FBFAFD" }}
                    onClick={onDeactivate}
                    disabled={busy || reason.trim().length < 20}
                    data-testid="admin-user-deactivate-confirm"
                  >
                    {busy ? "Working…" : "Confirm Deactivation"}
                  </button>
                </div>
              </div>
            )}

            {isDeactivated && !showReact && (
              <>
                <div
                  className="wn-card mt-3"
                  style={{ padding: 12, background: "rgba(154,160,192,0.10)", borderColor: "#9AA0C0" }}
                  data-testid="admin-user-deactivation-info"
                >
                  <div className="wn-label">Deactivated by</div>
                  <div
                    style={{
                      fontFamily: '"Fraunces", Georgia, serif',
                      fontWeight: 600,
                      fontSize: 14,
                      color: "#1B1F4B",
                      marginTop: 2,
                    }}
                  >
                    {u.deactivated_by_name || u.deactivated_by || "—"}
                  </div>
                  <div
                    style={{
                      fontFamily: '"Newsreader", Georgia, serif',
                      fontStyle: "italic",
                      color: "#6C6B85",
                      fontSize: 11.5,
                    }}
                  >
                    {relativeDate(u.deactivated_at)}
                  </div>
                  {u.deactivation_reason && (
                    <div style={{ color: "#272636", fontSize: 12.5, marginTop: 6, whiteSpace: "pre-wrap" }}>
                      {u.deactivation_reason}
                    </div>
                  )}
                </div>
                <button
                  className="wn-btn wn-btn-primary mt-3 w-full"
                  onClick={() => setShowReact(true)}
                  data-testid="admin-user-reactivate-open"
                >
                  Reactivate Account
                </button>
              </>
            )}

            {isDeactivated && showReact && (
              <div className="mt-3" data-testid="admin-user-reactivate-form">
                <p style={{ color: "#6C6B85", fontSize: 12.5 }}>
                  This restores the user's ability to sign in and act on the platform.
                </p>
                <label className="wn-label mt-3 block">Note (optional)</label>
                <textarea
                  className="wn-input"
                  style={{ minHeight: 70, resize: "vertical" }}
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  data-testid="admin-reactivate-note"
                />
                <div className="mt-3 flex justify-end gap-2">
                  <button
                    className="wn-btn wn-btn-tertiary"
                    onClick={() => { setShowReact(false); setReason(""); }}
                  >
                    Cancel
                  </button>
                  <button
                    className="wn-btn wn-btn-primary"
                    onClick={onReactivate}
                    disabled={busy}
                    data-testid="admin-user-reactivate-confirm"
                  >
                    {busy ? "Working…" : "Confirm Reactivation"}
                  </button>
                </div>
              </div>
            )}
          </div>
        </aside>
      </div>
    </section>
  );
}

function K({ label, v }) {
  return (
    <>
      <dt style={{ color: "#6C6B85" }}>{label}</dt>
      <dd style={{ fontWeight: 600, color: "#272636" }}>{v || "—"}</dd>
    </>
  );
}

function RoleContext({ role, ctx }) {
  if (role === "architect" && ctx.architect) {
    const a = ctx.architect;
    return (
      <>
        <dl className="mt-3 grid grid-cols-2 gap-y-2 gap-x-6" style={{ fontSize: 13 }}>
          <K label="Firm" v={a.firm} />
          <K label="License" v={a.license_no} />
          <K label="Service areas" v={(a.service_areas || []).join(", ")} />
          <K label="Verified" v={a.verified_badge ? "Yes" : "No"} />
          <K label="Designs" v={ctx.design_count} />
          <K label="Active projects" v={ctx.active_project_count} />
        </dl>
        <Link
          to={`/architects/${a.id}`}
          className="wn-btn wn-btn-tertiary mt-3"
          style={{ paddingLeft: 0 }}
        >
          View public profile <ChevronRight size={14} />
        </Link>
      </>
    );
  }
  if (role === "owner_builder") {
    return (
      <dl className="mt-3 grid grid-cols-3 gap-y-2 gap-x-6" style={{ fontSize: 13 }}>
        <K label="Saved designs" v={ctx.saved_designs} />
        <K label="Consultations" v={ctx.consultations} />
        <K label="Active projects" v={ctx.active_projects} />
      </dl>
    );
  }
  if (role === "city_staff") {
    return (
      <dl className="mt-3" style={{ fontSize: 13 }}>
        <K label="Design-review decisions logged" v={ctx.city_decisions_90d} />
      </dl>
    );
  }
  if (role === "platform_admin") {
    return (
      <dl className="mt-3" style={{ fontSize: 13 }}>
        <K label="Admin actions logged" v={ctx.admin_actions_90d} />
      </dl>
    );
  }
  return null;
}

function prettyAction(a) {
  if (!a) return "—";
  return a
    .replace(/_/g, " ")
    .replace(/\./g, " — ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}
